import { useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, TextField, Typography,
} from "@mui/material";

const CONFIRM_WORD = "DELETE";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Performs the deletion. Resolves on success, rejects on failure. */
  onConfirm: () => Promise<void>;
  loading: boolean;
  error: string | null;
  /** Set when the server refused because the user is on a team. */
  blocked: boolean;
  contactUrl: string;
}

/** Awaits a network call, so it owns loading/error and stays open on failure. */
export const DeleteAccountDialog = ({
  open, onClose, onConfirm, loading, error, blocked, contactUrl,
}: Props) => {
  const [typed, setTyped] = useState("");

  const handleClose = () => {
    if (loading) return;
    setTyped("");
    onClose();
  };

  const handleConfirm = async () => {
    try {
      // On success the caller signs the user out, so there is nothing to close.
      await onConfirm();
    } catch {
      // Surfaced via the `error` prop; stay open so they can retry.
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Delete your account</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {blocked ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Account deletion isn’t available for team accounts yet — contact support.
              </Typography>
              <Link href={contactUrl} target="_blank" rel="noopener" variant="body2">
                Contact Us
              </Link>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                This cannot be undone. Your boards, concepts, collections, schedules and uploads
                will be permanently deleted, and any active subscription will be cancelled.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type <strong>{CONFIRM_WORD}</strong> to confirm.
              </Typography>
              <TextField
                size="small"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={loading}
                placeholder={CONFIRM_WORD}
                inputProps={{
                  "aria-label": `Type ${CONFIRM_WORD} to confirm`,
                  "data-testid": "delete-account-confirm-input",
                }}
              />
              {error && (
                <Typography variant="body2" color="error" data-testid="delete-account-error">
                  {error}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {blocked ? "Close" : "Keep my account"}
        </Button>
        {!blocked && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            disabled={loading || typed.trim().toUpperCase() !== CONFIRM_WORD}
            data-testid="confirm-delete-account"
          >
            {loading ? "Deleting…" : "Delete account"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
